// src/pages/Settings/BusinessHours/index.tsx (או BusinessHoursPage.tsx לפי המבנה אצלך)

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/auth/hooks';
import { supabase } from '../../../lib/supabase';
import { DayHours } from './components/DayHours';
import { SpecialDateCard } from './components/SpecialDateCard';
import { SpecialDateFormModal } from './components/SpecialDateFormModal';
import { DAYS, DEFAULT_SPECIAL_DATE } from './constants';
import { BusinessHoursData, Break } from './types';
import { validateBreaks } from './utils/validation';
import { useUnsavedChanges } from './hooks/useUnsavedChanges';
import { convertToUI, convertToDB, validateBusinessHours } from './utils/dataTransform';
import toast from 'react-hot-toast';

function BusinessHoursPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // הישות הראשית שמגיעה/נשמרת למסד
  const [hours, setHours] = useState<BusinessHoursData | null>(null);

  // טופס "יום מיוחד"
  const [showSpecialDateForm, setShowSpecialDateForm] = useState(false);
  const [specialDateFormData, setSpecialDateFormData] = useState(DEFAULT_SPECIAL_DATE);

  // מעקב אחרי שינויים שלא נשמרו
  const { hasUnsavedChanges, setHasUnsavedChanges } = useUnsavedChanges();

  // הפסקות לכל יום לשימוש פנימי (נשמר כ-snake_case ליישור עם DB)
  const [breaksByDay, setBreaksByDay] = useState<{ [day: string]: Break[] }>({});

  useEffect(() => {
    const initializeBusinessHours = async () => {
      if (!user?.id) return;

      console.log('🟣 [BusinessHours/init] Starting initialization');

      // שלב 1: בדיקה אם יש שעות ב-localStorage שצריך לסנכרן
      const registrationDraft = localStorage.getItem('registration_draft');

      if (registrationDraft) {
        try {
          const parsedDraft = JSON.parse(registrationDraft);
          const hoursData = parsedDraft.step3?.hours;

          if (hoursData) {
            console.log('🟣 [BusinessHours/init] Found hours in localStorage, syncing first');

            const { data: userData, error: userError } = await supabase
              .from('users')
              .select('business_id')
              .eq('id', user?.id)
              .single();

            if (userError || !userData?.business_id) {
              throw new Error('לא נמצא עסק מקושר');
            }

            const businessId = userData.business_id;

            const formattedHours = Object.entries(hoursData).reduce((acc, [day, details]) => {
              acc[day] = {
                is_active: details.isActive,
                start_time: details.startTime,
                end_time: details.endTime,
                breaks: (details.breaks || []).map((b: any) => ({
                  id: b.id,
                  start_time: b.startTime,
                  end_time: b.endTime
                }))
              };
              return acc;
            }, {} as BusinessHoursData['regular_hours']);

            const businessHoursData: BusinessHoursData = {
              business_id: businessId,
              regular_hours: formattedHours,
              special_dates: []
            };

            console.log('🟣 [BusinessHours/init] Syncing hours from localStorage:', businessHoursData);

            const { error } = await supabase
              .from('business_hours')
              .upsert([businessHoursData], { onConflict: 'business_id' });

            if (error) {
              console.error('🔴 [BusinessHours/init] Error syncing from localStorage:', error);
              throw error;
            }

            console.log('🟣 [BusinessHours/init] Hours synced successfully from localStorage');
            toast.success('שעות הפעילות נוספו או עודכנו בהצלחה');
            localStorage.removeItem('registration_draft');
          }
        } catch (error: any) {
          console.error('🔴 [BusinessHours/init] Failed to sync from localStorage:', error);
          // ממשיכים לטעינה רגילה גם אם הסנכרון נכשל
        }
      }

      // שלב 2: טעינת השעות מה-DB (אחרי הסנכרון)
      await loadBusinessHours();
    };

    initializeBusinessHours();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const loadBusinessHours = async () => {
    try {
      setError(null);
      setLoading(true);

      console.log('🟣 [BusinessHours/loadBusinessHours] Starting to load hours');

      // שליפת business_id למשתמש
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('business_id')
        .eq('id', user?.id)
        .single();

      if (userError || !userData?.business_id) {
        throw new Error('לא נמצא עסק מקושר');
      }

      console.log('🟣 [BusinessHours/loadBusinessHours] business_id:', userData.business_id);

      // שליפת business_hours
      const { data, error } = await supabase
        .from('business_hours')
        .select('*')
        .eq('business_id', userData.business_id)
        .single();

      if (error) {
        console.log('🟣 [BusinessHours/loadBusinessHours] Error loading hours:', error);
        if ((error as any).code === 'PGRST116') {
          console.log('🟣 [BusinessHours/loadBusinessHours] No hours found, creating defaults');
          // אין רשומה — ניצור ברירת מחדל
          const defaultRegularHours: BusinessHoursData['regular_hours'] = DAYS.reduce((acc, day) => ({
            ...acc,
            [day.key]: {
              is_active: day.id !== 6, // שבת (id=6) לא פעיל כברירת מחדל
              start_time: '09:00',
              end_time: '17:00',
              breaks: []
            }
          }), {} as BusinessHoursData['regular_hours']);

          const defaultHours: Partial<BusinessHoursData> = {
            business_id: userData.business_id,
            regular_hours: defaultRegularHours,
            special_dates: []
          };

          // שימוש ב-upsert במקום insert כדי למנוע duplicate key errors
          const { data: newData, error: upsertError } = await supabase
            .from('business_hours')
            .upsert([defaultHours], { onConflict: 'business_id' })
            .select()
            .single();

          if (upsertError) {
            console.error('🔴 [BusinessHours/loadBusinessHours] Error creating default hours:', upsertError);
            throw upsertError;
          }

          console.log('🟣 [BusinessHours/loadBusinessHours] Default hours created/updated successfully');
          setHours(newData as BusinessHoursData);
          setBreaksByDay(
            Object.fromEntries(
              Object.entries(defaultRegularHours).map(([k, v]: any) => [k, v.breaks || []])
            )
          );
        } else {
          throw error;
        }
      } else {
        console.log('🟣 [BusinessHours/loadBusinessHours] Hours loaded successfully from DB');
        console.log('🟣 [BusinessHours/loadBusinessHours] Hours data:', data);
        setHours(data as BusinessHoursData);

        // איסוף הפסקות ל-map פנימי (snake_case מהשרת) — נשמור אחיד
        if (data?.regular_hours) {
          const breaksMap: { [day: string]: Break[] } = {};
          Object.entries(data.regular_hours).forEach(([dayKey, dayObj]: any) => {
            breaksMap[dayKey] = (dayObj?.breaks || []) as Break[];
          });
          setBreaksByDay(breaksMap);
        }
      }
    } catch (err: any) {
      console.error('🔴 [BusinessHours/loadBusinessHours] Failed to load hours:', err);
      setError(err.message || 'שגיאה בטעינת שעות הפעילות');
    } finally {
      setLoading(false);
    }
  };

  // שינוי שדה של יום מסוים (יום ← (isActive/startTime/endTime))
  const handleHoursChange = (
    dayKey: keyof BusinessHoursData['regular_hours'],
    field: string,
    value: any
  ) => {
    if (!hours) return;

    // מיפוי שמות ל-snake_case (DB)
    let realField = field;
    if (field === 'isActive') realField = 'is_active';
    if (field === 'startTime') realField = 'start_time';
    if (field === 'endTime') realField = 'end_time';

    const updatedHours: BusinessHoursData = {
      ...hours,
      regular_hours: {
        ...hours.regular_hours,
        [dayKey]: {
          ...hours.regular_hours[dayKey],
          [realField]: value
        }
      }
    };

    setHours(updatedHours);
    setHasUnsavedChanges(true);
  };

  // הוספת הפסקה — נשמור ב-snake_case כדי להיות אחידים מול DB
  const handleAddBreak = (day: string, startTime: string, endTime: string) => {
    const newBreak: Break = {
      id: Date.now().toString(),
      start_time: startTime,
      end_time: endTime
    };

    // עדכון breaksByDay
    setBreaksByDay(prev => {
      const updatedBreaks = [...(prev[day] || []), newBreak];

      // סנכרון לשדה breaks בתוך hours.regular_hours[day]
      setHours(prevHours => {
        if (!prevHours) return prevHours;
        return {
          ...prevHours,
          regular_hours: {
            ...prevHours.regular_hours,
            [day]: {
              ...prevHours.regular_hours[day as keyof BusinessHoursData['regular_hours']],
              breaks: updatedBreaks
            }
          }
        } as BusinessHoursData;
      });

      return {
        ...prev,
        [day]: updatedBreaks
      };
    });

    setHasUnsavedChanges(true);
  };

  const handleDeleteBreak = (day: string, breakId: string) => {
    setBreaksByDay(prev => {
      const updatedBreaks = (prev[day] || []).filter(b => b.id !== breakId);

      // סנכרון ל-hours.regular_hours
      setHours(prevHours => {
        if (!prevHours) return prevHours;
        return {
          ...prevHours,
          regular_hours: {
            ...prevHours.regular_hours,
            [day]: {
              ...prevHours.regular_hours[day as keyof BusinessHoursData['regular_hours']],
              breaks: updatedBreaks
            }
          }
        } as BusinessHoursData;
      });

      return {
        ...prev,
        [day]: updatedBreaks
      };
    });

    setHasUnsavedChanges(true);
  };

  const handleAddSpecialDate = () => {
    if (!hours) return;

    const updatedHours: BusinessHoursData = {
      ...hours,
      special_dates: [
        ...(hours.special_dates || []),
        {
          ...specialDateFormData,
          id: crypto.randomUUID()
        }
      ]
    };

    setHours(updatedHours);
    setShowSpecialDateForm(false);
    setSpecialDateFormData(DEFAULT_SPECIAL_DATE);
    setHasUnsavedChanges(true);
  };

  const handleDeleteSpecialDate = (id: string) => {
    if (!hours) return;

    const updatedHours: BusinessHoursData = {
      ...hours,
      special_dates: (hours.special_dates || []).filter(date => date.id !== id)
    };

    setHours(updatedHours);
    setHasUnsavedChanges(true);
  };

  const handleSave = async () => {
    if (!hours || !user?.id || isSaving) {
      return;
    }

    if (!hasUnsavedChanges) {
      toast('אין שינויים לשמור', { icon: 'ℹ️' });
      return;
    }

    // Validate before saving
    const validationError = validateBusinessHours(hours);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    try {
      setIsSaving(true);

      const { data: userData, error: userError} = await supabase
        .from('users')
        .select('business_id')
        .eq('id', user.id)
        .single();

      if (userError || !userData?.business_id) {
        throw new Error('לא נמצא עסק מקושר');
      }

      const dataToSave: BusinessHoursData = {
        id: hours.id,
        business_id: userData.business_id,
        regular_hours: hours.regular_hours,
        special_dates: hours.special_dates || []
      };

      const { data, error } = await supabase
        .from('business_hours')
        .upsert(dataToSave)
        .select()
        .single();

      if (error) throw error;

      toast.success('שעות הפעילות נשמרו בהצלחה');
      setHasUnsavedChanges(false);

      await loadBusinessHours();
    } catch (err: any) {

      toast.error(err.message || 'שגיאה בשמירת שעות הפעילות');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px] pb-24">
        <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4 pb-24">
        <div className="text-red-600 font-medium">{error}</div>
        <button
          onClick={loadBusinessHours}
          className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700"
        >
          נסה שוב
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="sticky top-0 z-10 bg-gray-50 px-4 pt-4 pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/settings" className="text-gray-500 active:text-gray-700">
              <ArrowRight className="h-6 w-6" />
            </Link>
            <h1 className="text-2xl font-bold text-gray-900">שעות פעילות</h1>
          </div>
          {hasUnsavedChanges && (
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={handleSave}
              disabled={isSaving}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg active:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSaving ? 'שומר...' : 'שמור'}
            </motion.button>
          )}
        </div>
      </div>

      <div className="px-4 space-y-3">
        {DAYS.map((day) => {
          const dayHours = hours?.regular_hours[day.key as keyof BusinessHoursData['regular_hours']];
          if (!dayHours) return null;

          // המרה בטוחה (fallback לשני הפורמטים) — שולחים לקומפ' DayHours ערכים נקיים בלבד
          const dayHoursCamel = {
            isActive: (dayHours as any)?.is_active ?? (dayHours as any)?.isActive ?? false,
            startTime: (dayHours as any)?.start_time ?? (dayHours as any)?.startTime ?? '',
            endTime:   (dayHours as any)?.end_time   ?? (dayHours as any)?.endTime   ?? '',
            breaks:    (dayHours as any)?.breaks ?? [] // DayHours כבר מנרמל snake/camel להפסקות
          };

          // בוחרים את מקור ההפסקות (העדפה ל-breaksByDay שנשמר אצלנו מעודכן תמיד)
          const dayBreaks: Break[] = breaksByDay[day.key] ?? (dayHours as any)?.breaks ?? [];

          return (
            <DayHours
              key={day.id}
              dayName={day.name}
              hours={dayHoursCamel}
              breaks={dayBreaks}
              onHoursChange={(field, value) =>
                handleHoursChange(day.key as keyof BusinessHoursData['regular_hours'], field, value)
              }
              onAddBreak={(startTime, endTime) => handleAddBreak(day.key, startTime, endTime)}
              onDeleteBreak={(breakId) => handleDeleteBreak(day.key, breakId)}
            />
          );
        })}
      </div>

      <div className="bg-white rounded-xl mx-4 shadow-sm">
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">ימים מיוחדים</h2>
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowSpecialDateForm(true)}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg active:bg-blue-700 transition-colors"
          >
            הוסף יום מיוחד
          </motion.button>
        </div>

        <div className="p-4 space-y-3">
          {(hours?.special_dates || [])
            .filter(dateObj => {
              // הצג רק ימים מיוחדים שטרם חלפו (היום ומעלה)
              const today = new Date();
              const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
              const specialDate = new Date(dateObj.date);
              specialDate.setHours(0, 0, 0, 0);
              todayDate.setHours(0, 0, 0, 0);
              return specialDate.getTime() >= todayDate.getTime();
            })
            .map((date) => (
              <SpecialDateCard
                key={date.id}
                date={date}
                onDelete={handleDeleteSpecialDate}
              />
            ))}
        </div>

        {(!hours?.special_dates ||
          hours.special_dates.filter(dateObj => {
            const today = new Date();
            const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
            const specialDate = new Date(dateObj.date);
            specialDate.setHours(0, 0, 0, 0);
            todayDate.setHours(0, 0, 0, 0);
            return specialDate.getTime() >= todayDate.getTime();
          }).length === 0) && (
          <div className="text-center py-8">
            <p className="text-sm text-gray-500">לא הוגדרו ימים מיוחדים</p>
          </div>
        )}
      </div>

      <AnimatePresence>
        {showSpecialDateForm && (
          <SpecialDateFormModal
            formData={specialDateFormData}
            onFormChange={(data) =>
              setSpecialDateFormData(prev => ({
                ...prev,
                ...data
              } as typeof DEFAULT_SPECIAL_DATE))
            }
            onClose={() => {
              setShowSpecialDateForm(false);
              setSpecialDateFormData(DEFAULT_SPECIAL_DATE);
            }}
            onSubmit={handleAddSpecialDate}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

export default BusinessHoursPage;
